/**
 * Date-sort utilities for financial / compliance report tables.
 *
 * Per Governance Amendment 5 (Primary Date Governance), financial surfaces
 * default-sort by their primary business date (server-side). When the
 * operator wants to explore the data by a different date axis, this helper
 * provides a deterministic client-side sort with the canonical
 * `created_at DESC` tiebreaker — same shape as the exemplary backend pattern
 * `routes_bank_transactions.py:255` `.sort([("txn_date", -1), ("created_at", -1)])`.
 *
 * Usage:
 *
 *   const [sortState, setSortState] = useState(null);  // null = backend default
 *   const sortedItems = useMemo(
 *       () => sortByDate(data.items, sortState),
 *       [data.items, sortState],
 *   );
 *
 *   <SortableHeader
 *       field="invoice_date"
 *       label="Invoice Date"
 *       sortState={sortState}
 *       onSort={setSortState}
 *   />
 */
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

const _toTimestamp = (v) => {
    if (!v) return null;
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : null;
};

/**
 * Stable sort by a date field with `created_at` tiebreaker.
 *
 * @param {Array<object>} items   The list to sort.
 * @param {{field: string, dir: "asc"|"desc"} | null} sortState
 * @returns {Array<object>}       Sorted copy (does not mutate input).
 *                                Returns input as-is if sortState is null.
 */
export function sortByDate(items, sortState) {
    if (!Array.isArray(items) || !sortState || !sortState.field) return items;
    const { field, dir } = sortState;
    const mul = dir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
        const ax = _toTimestamp(a?.[field]);
        const bx = _toTimestamp(b?.[field]);
        // nulls always trail (matches Mongo "missing" behaviour)
        if (ax === null && bx === null) {
            // both null on primary → tiebreaker on created_at
        } else if (ax === null) {
            return 1;
        } else if (bx === null) {
            return -1;
        } else if (ax !== bx) {
            return (ax - bx) * mul;
        }
        // Amendment 5 universal tiebreaker — created_at DESC
        const ay = _toTimestamp(a?.created_at);
        const by = _toTimestamp(b?.created_at);
        if (ay === null && by === null) return 0;
        if (ay === null) return 1;
        if (by === null) return -1;
        return by - ay;
    });
}

/**
 * Click-to-sort table header cell. Visually mirrors the existing
 * `reedos-eyebrow text-[9px]` thead style so it integrates without
 * a redesign.
 *
 * Sort cycle on click: backend-default → desc → asc → backend-default
 */
export function SortableHeader({ field, label, sortState, onSort, className = "", align = "left", testid }) {
    const active = sortState && sortState.field === field;
    const dir = active ? sortState.dir : null;
    const cycle = () => {
        if (!active) onSort({ field, dir: "desc" });
        else if (dir === "desc") onSort({ field, dir: "asc" });
        else onSort(null);
    };
    const arrowIcon = !active
        ? <CaretUpDown size={10} className="text-slate-400" />
        : dir === "desc"
            ? <CaretDown size={10} weight="bold" className="text-slate-900" />
            : <CaretUp size={10} weight="bold" className="text-slate-900" />;
    const justify = align === "right" ? "justify-end" : "justify-start";
    return (
        <th className={`px-4 py-2 reedos-eyebrow text-[9px] ${className}`}>
            <button
                type="button"
                onClick={cycle}
                className={`inline-flex items-center gap-1 w-full ${justify} hover:text-slate-900 transition-colors ${active ? "text-slate-900" : ""}`}
                data-testid={testid}
                title={active ? `Sorted ${dir === "desc" ? "newest first" : "oldest first"} — click to ${dir === "desc" ? "reverse" : "clear"}` : "Click to sort by this date"}
            >
                <span>{label}</span>
                {arrowIcon}
            </button>
        </th>
    );
}
